import * as React from 'react';
import * as UI from 'core/ui';
import * as Images from 'pos/Images';
import { AddressDTO, BuyerCustomerDTO, PaymentMethodDTO, Res } from 'core/api';
import { ActionButtonDefinition, Keypad, PosScreenClassComponent, ScreenSize, ThemeManager } from 'pos/ui';
import * as Components from './components';
import * as PaymentComponents from '../payments-processing/components';
import { ValidationErrors, Validator } from 'core/infrastructure';
import { Column, Row } from 'core/ui';
import * as PrepaymentStatus from '../payments-processing/PrepaymentStatus';
import { Modal } from 'core/ui-next/modal';

export type DeliveryNoteConfirmedData = {
	notes: string;
	method: PaymentMethodDTO;
	prepayAmount: number;
	paidAmount: number;
	deliveryAddress: AddressDTO;
	customerDocumentNumber: string;
};

type Props = {
	isRefund: boolean;
	notes: string;
	deliveryAddress: Nullable<AddressDTO>;
	customer: BuyerCustomerDTO;
	onCustomerChanged: (customer: BuyerCustomerDTO) => Promise<void>;
	getPendingAmount: () => number;
	maxAvailableChange: number;
	paymentMethods: PaymentMethodDTO[];
} & Modal.Actions<DeliveryNoteConfirmedData>;


export type State = {
	notes: string;
	currentCustomer: BuyerCustomerDTO;
	prepaymentData: PrepaymentStatus.Status;
	deliveryAddress: AddressDTO;
	customerDocumentNumber: string;
}

export const validate = async (model: State) => {

	const paymentErrors = {};
	const { ErrorMessage } = await PrepaymentStatus.check(model.prepaymentData);
	if (ErrorMessage)
		ValidationErrors.add(paymentErrors, 'prepaymentData', ErrorMessage);

	const errors = {};
	if (model.currentCustomer === null || model.currentCustomer.Id === 0)
		ValidationErrors.add(errors, 'currentCustomer', Res.Msg.MSG_CUSTOMER_MUST_BE_SELECTED());

	return ValidationErrors.concat(errors, paymentErrors);
};

export default class ConfirmDeliveryNoteDataScreen extends PosScreenClassComponent<Props, State> {

	private form = React.createRef<UI.Form<State>>();

	protected getScreenId(): string {
		return 'CONFIRM_DELIVERY_NOTE_DATA';
	}

	protected getTitle(): string {
		return this.props.isRefund ? Res.Web.CONFIRM_DOCUMENT_DATA_CUSTOMER_LBL_REFUND_DELIVERY_NOTE_TITLE() : Res.Web.CONFIRM_DOCUMENT_DATA_CUSTOMER_LBL_CREATE_DELIVERY_NOTE_TITLE();
	}

	protected getImage(): string | SvgIcon {
		return Images.CreateDeliveryNote;
	}

	protected getSize(): ScreenSize {
		return { width: 1000, height: 768 };
	}

	protected getActionButtonsDefs(): Nullable<ActionButtonDefinition>[] {
		return [
			this.cancelButton(),
			null,
			null,
			null,
			null,
			this.okButton(this.onOK)
		];
	}

	protected useKeypad = () => !ThemeManager.isLargeDevice();

	private onOK = async () => {

		const valid = await this.form.current!.updateModel();
		if (valid) {

			// Aunque ya se ha estado validando, volvemos a comprobar por si el saldo del TPV se pudiera
			// quedar en negativo
			const canPay = await PrepaymentStatus.canPay(this.state.prepaymentData);
			if (!canPay)
				return;

			this.close({
				notes: this.state.notes,
				method: this.state.prepaymentData.Method,
				prepayAmount: this.state.prepaymentData.AmountToPay,
				paidAmount: this.state.prepaymentData.PaidAmount,
				deliveryAddress: this.state.deliveryAddress,
				customerDocumentNumber: this.state.customerDocumentNumber
			});
		}
	};

	protected getContent(): React.ReactNode {
		const { Form } = UI.controlsFor<State>();
		const { Customer, Address, Notes, CustomerDocumentNumber } = Components.createFor<State>();
		const { Prepayment } = PaymentComponents.createFor<State>();

		const onCustomerChanged = async (customer: BuyerCustomerDTO) => {
			await this.props.onCustomerChanged(customer);
			this.setState({ deliveryAddress: customer.Address, currentCustomer: customer }, async () => {
				await this.form.current!.remount('deliveryAddress');
				// Limpiamos errores
				await this.form.current!.updateModel();
			});
		};

		const hasCustomer = this.state.currentCustomer?.Id > 0;

		return <Form className='m-t m-r m-l' ref={this.form} model={this.state} validator={Validator.create(state => validate(state))}>
			<Row>
				<Column width='one-half'>
					<div>
						<Customer linkTo={x => x.currentCustomer} onCustomerChanged={onCustomerChanged} />
					</div>
					{hasCustomer &&
						<div className='m-t-md'>
							<Address linkTo={x => x.deliveryAddress} customer={this.state.currentCustomer} />
						</div>
					}
					<div className='m-t-md'>
						<Notes linkTo={x => x.notes} />
					</div>

					<div className='m-t-md'>
						<CustomerDocumentNumber linkTo={x => x.customerDocumentNumber} />
					</div>
				</Column>
				<Column width='one-half'>
					<Prepayment
						linkTo={x => x.prepaymentData}
						pendingAmount={this.props.getPendingAmount()}
						methods={this.props.paymentMethods}
						isRefund={this.props.isRefund}
					/>
					{
						// Lo ideal sería hacer esto responsive y que saliese si y solo si hay dos columnas, pero
						// no tengo una forma fácil de hacerlo de forma coordinada con el useKeypad del PosScreenClassComponent,
						// así que me conformo con asumir que si es pantalla grande (Tablet/PC), habrá dos columnas.
						// Es mucho suponer, pero no tengo muchas más opciones ahora.
						ThemeManager.isLargeDevice() && <Keypad isFullScreen={true} isLargeDevice={ThemeManager.isLargeDevice()} alwaysVisible />
					}
				</Column>
			</Row>
		</Form >;
	}

	createInitialState(props: Props): Nullable<State> {
		return {
			notes: props.notes,
			currentCustomer: this.props.customer,
			prepaymentData: {
				PendingAmount: 0,
				PaidAmount: 0,
				AmountToPay: 0,
				Method: this.props.paymentMethods[0] || null,
				ChangeAmount: 0,
				TipAmount: 0,
				ThereIsEnoughChange: true,
				ErrorMessage: ''
			},
			deliveryAddress: this.props.deliveryAddress || this.props.customer.Address,
			customerDocumentNumber: ''
		};
	}

	onComponentDidMount(): void {
	}

	onComponentWillUnmount(): void {
	}
}
